import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject, Observable } from 'rxjs';

import { environment } from 'environments/environment';
import { AuthData } from '../models/auth-model';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { MatDialog } from '@angular/material';
import { map, retry } from 'rxjs/operators';
import { Usuario } from 'app/main/utilerias/models/usuarios/usuario';
import { Permiso } from 'app/main/utilerias/models/usuarios/permiso';

const BACKEND_URL = environment.apiUrl + '/';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isAuthenticated = false;
  private token: string;
  private tokenTimer: any;
  private userId: string;
  private userName: string;
  private authStatusListener = new Subject<boolean>();
  private permisos: Permiso[];
  private usuario: Usuario;

  constructor(private http: HttpClient, private router: Router,
    private _fuseSplashScreenService: FuseSplashScreenService,
    private _dialogService: MatDialog) { }

  /**
   * Ontenemos el nombre del usuario.
   */
  getUserName(): string {
    return this.userName;
  }

  /**
   * Obtenemos el token.
   */
  getToken(): string {
    return this.token;
  }


  /**
   * Ontenemos el nombre del usuario.
   */
  obtenerUsuario(): Usuario {
    return this.usuario;
  }

  /**
   * Obtenemos el token.
   */
  obtenerPermisos(): Permiso[] {
    return this.permisos;
  }

  /**
   * Obtiene el permiso de la opcion seleccionada.
   * @param opcion que se desea consultar
   */
  obtenerPermisoPorOpcion(opcion: string): Permiso{
    return this.permisos.filter(permiso => permiso.opcion ===  opcion)[0];
  }


  /**
   * Obtenemos true si el usuario esta autentiado false si no esta.
   */
  getIsAuth(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Obtenemos el id del usuario.
   */
  getUserId(): string {
    return this.userId;
  }

  /**
   * Retornamos un observable el cual proveera de la informacion del usuario en determinado momento.
   */
  getAuthStatusListener(): Observable<any> {
    return this.authStatusListener.asObservable();
  }

  /**
   * Crea el usuario para guardarlo en la base
   * @param email del usuario
   * @param name del usuario
   * @param password del usuario
   */
  createUser(email: string, name: string, password: string): void {
    const authData: AuthData = { usua: email, pass: password };
    this.http.post(BACKEND_URL + 'signup', authData).subscribe(
      (response) => {
        this.router.navigate(['/']);
      },
      error => {
        this.authStatusListener.next(false);
      }
    );
  }

  /**
   * Intentamos logearnos.
   * @param email del usuario
   * @param password del usuario
   */
  login(email: string, password: string): void {
    const authData: AuthData = { usua: email, pass: password };
    this.http
      .post<any>(
        BACKEND_URL + 'mms/login',
        authData
      )
      .subscribe(
        response => {
          const token = response.token;
          this.token = token;
          if (token) {
            this._fuseSplashScreenService.show();
            const expiresInDuration = response.expiresIn;
            this.setAuthTimer(expiresInDuration);
            this.isAuthenticated = true;
            this.userId = response.result[0].id;
            this.authStatusListener.next(true);
            const now = new Date();
            this.usuario = response.result[0];
            this.userName = response.result[0].usua;
            this.permisos = response.result[1].transacciones;
            const expirationDate = new Date(
              now.getTime() + expiresInDuration * 1000
            );
            this.saveAuthData(token, expirationDate, this.userId, response.result[0].usua, response.result[0], response.result[1].transacciones);
            this.router.navigate(['/catalogos/ramos']);
            this._fuseSplashScreenService.hide();
          }
        },
        error => {
          this._fuseSplashScreenService.hide();
          this.authStatusListener.next(false);
        }
      );
  }

  autoAuthUser(): void {
    const authInformation = this.getAuthData();
    if (!authInformation) {
      this.logout();
    }
    const now = new Date();
    const expiresIn = authInformation.expirationDate.getTime() - now.getTime();
    if (expiresIn > 0) {
      this.token = authInformation.token;
      this.isAuthenticated = true;
      this.userId = authInformation.userId;
      this.setAuthTimer(expiresIn / 1000);
      this.authStatusListener.next(true);
      
      this.userName = localStorage.getItem('userName');
      this.permisos = JSON.parse(localStorage.getItem('permisos'));
      this.usuario = JSON.parse(localStorage.getItem('usuario'));
    }
  }

  /**
   * Aqui mandamos falso y avisamos que el usuario no esta autenticado
   */
  logout(): void {
    this.token = null;
    this.isAuthenticated = false;
    this.authStatusListener.next(false);
    this.userId = null;
    this.permisos = null;
    this.usuario = null;
    clearTimeout(this.tokenTimer);
    this.clearAuthData();
    this.router.navigate(['/auth/login']);
  }

  /**
   * Crea una funcion de timer para que cuando pase el tiempo del logeo el usuario salga de la app
   * @param duration la duracion que se desa que el usuario permanezca logeado
   */
  private setAuthTimer(duration: number): void {
    this.tokenTimer = setTimeout(() => {

      // this._dialogService.open(ErrorComponent, { data: { message: 'Your session has expired, please login again!', title: 'Auto logout' } });
      
      this.logout();
    }, duration * 1000);
  }


  /**
   * Guarda la informacion del usuario en el local storage.
   * @param token token que se guardara en el localstorage
   * @param expirationDate la fecha de expiracion que tendra el token.
   * @param userId el id del usuario generado.
   * @param userName el nombre del usuario.
   */
  private saveAuthData(token: string, expirationDate: Date, userId: string, userName: string, usuario, permisos): void {
    localStorage.setItem('usuario', JSON.stringify(usuario));
    localStorage.setItem('permisos', JSON.stringify(permisos));
    localStorage.setItem('token', token);
    localStorage.setItem('expiration', expirationDate.toISOString());
    localStorage.setItem('userId', userId);
    localStorage.setItem('userName', userName);
  }

  /**
   * Limpiamos la informacion del usuario.
   */
  private clearAuthData(): void {
    localStorage.removeItem('usuario');
    localStorage.removeItem('permisos');
    localStorage.removeItem('token');
    localStorage.removeItem('expiration');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('permisos');
    
  }


  /**
   * Cuando queremos obtener la informacion de autenticacion del usuario ejecutamos este metodo.
   */
  private getAuthData(): null | { token: string, expirationDate: Date, userId: string } {
    const token = localStorage.getItem('token');
    const expirationDate = localStorage.getItem('expiration');
    const userId = localStorage.getItem('userId');
    if (!token || !expirationDate) {
      return;
    }
    return {
      token: token,
      expirationDate: new Date(expirationDate),
      userId: userId
    };
  }

  /**
   * Este metodo valida si el usuario existe en base de datos o no, si existe.
   * @param usuario que se desea consultar
   */
  public validaUsuario(usuario: string): any {
    if (usuario.length > 0) {
      return this.http.get(BACKEND_URL + 'mms/validar/' + usuario).pipe(map((data: { flag: boolean, message: string, result: any }) => {

        let responseUser: Usuario;
        let transacciones;
        const result = [];

        if (data.result) {
          responseUser = data.result[0];
          transacciones = data.result[1];

          const transformado = {
            usua: responseUser.usua,
            procc: responseUser.procc,
            pass: responseUser.pass,
            pass1: responseUser.pass,
            agente: responseUser.agente,
            su_id: responseUser.su_id,
            email: responseUser.email,
            alert_cancelacion: responseUser.alert_cancelacion <= 0 ? false : true,
            alert_diasreno: responseUser.alert_diasreno <= 0 ? '' : responseUser.alert_diasreno,
            alert_diascobra: responseUser.alert_diascobra <= 0 ? '' : responseUser.alert_diasreno,
            id: responseUser.id
          };

          result.push(transformado);
          result.push(transacciones);
        }
        return {
          flag: data.flag,
          message: data.message,
          result: result
        };
      }));
    }
  }

  obtenerUsuarios(): Observable<any> {
     
    return this.http.get(BACKEND_URL + 'mms/todos');
  }
}
